import { useMutation } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { downloadBlob } from '@common/utils/downloadBlob';
import { apiClient } from '@config/server/apiClient';
import { teamEndpoints } from '@config/server/apiEndpoints';

/**
 * Export team members request parameters
 */
export interface ExportTeamMembersParams {
	memberIds?: string[];
}

/**
 * API function to export team members as CSV
 * Downloads the file to the user's machine
 */
export const exportTeamMembers = async (
	servicesProviderId: string,
	params: ExportTeamMembersParams,
): Promise<void> => {
	const { memberIds } = params;

	const blob = await apiClient.postBlob(teamEndpoints.export(servicesProviderId), {
		memberIds,
	});

	downloadBlob(blob, 'team-members.csv');
};

/**
 * Hook for exporting team members as CSV
 */
export const useExportTeamMembers = (): ReturnType<typeof useMutation<void, Error, ExportTeamMembersParams>> => {
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (params: ExportTeamMembersParams) => {
			if (!servicesProviderId) {
				throw new Error('Cannot export team members without services provider ID');
			}
			return exportTeamMembers(servicesProviderId, params);
		},
	});
};
